/**
 * 统一请求封装（原生 fetch，零依赖，对齐 admin 的 Result 解包语义）。
 *
 * 约定：
 * - 所有路径相对于网关：本文件统一前缀 `/api`（经 vite.config.ts 的 server.proxy 转发到网关 :8080）。
 * - 请求拦截注入 Authorization: Bearer <token>（localStorage['bluplanet_field_token']）。
 * - 响应统一解析 Result{code,message,data}：code===200 取 data；否则以 message 抛错。
 * - 401 自动清 token，调用方捕获后引导重新登录。
 */

const TOKEN_KEY = 'bluplanet_field_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  params?: Record<string, unknown>;
  body?: unknown;
}

function buildQuery(params?: Record<string, unknown>): string {
  if (!params) return '';
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
  return qs ? `?${qs}` : '';
}

function resolveUrl(url: string): string {
  const normalized = url.startsWith('/') ? url : `/${url}`;
  return `/api${normalized}`;
}

export async function request<T = unknown>(url: string, options: RequestOptions = {}): Promise<T> {
  const { params, body, headers, ...rest } = options;
  const fullUrl = resolveUrl(url) + buildQuery(params);

  const reqHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(headers as Record<string, string> | undefined),
  };
  const token = getToken();
  if (token) reqHeaders.Authorization = `Bearer ${token}`;

  const resp = await fetch(fullUrl, {
    ...rest,
    headers: reqHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const text = await resp.text();
  let json: unknown = text;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = text;
    }
  }

  if (resp.status === 401) {
    clearToken();
    throw Object.assign(new Error('登录已过期，请重新登录'), { status: 401 });
  }

  if (json && typeof json === 'object' && !Array.isArray(json) && typeof (json as { code?: unknown }).code === 'number') {
    const result = json as { code: number; message?: string; data?: T };
    if (result.code !== 200) {
      throw Object.assign(new Error(result.message || '请求失败'), { status: resp.status, code: result.code });
    }
    return result.data as T;
  }

  // 非标准响应（如直接返回文本 / 字节流）
  return json as T;
}

/**
 * 文件上传专用：multipart/form-data，不使用 JSON。
 * 网关路由 /api/file/** → blu-trade /file/**（StripPrefix=1）。
 */
export async function uploadFile<T = unknown>(
  url: string,
  formData: FormData,
  options: Omit<RequestOptions, 'body' | 'params'> = {},
): Promise<T> {
  const fullUrl = resolveUrl(url);
  const reqHeaders: Record<string, string> = { ...(options.headers as Record<string, string> | undefined) };
  const token = getToken();
  if (token) reqHeaders.Authorization = `Bearer ${token}`;

  const resp = await fetch(fullUrl, {
    ...options,
    method: options.method ?? 'POST',
    headers: reqHeaders,
    body: formData,
  });

  const text = await resp.text();
  let json: unknown = text;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = text;
    }
  }

  if (resp.status === 401) {
    clearToken();
    throw Object.assign(new Error('登录已过期，请重新登录'), { status: 401 });
  }

  if (json && typeof json === 'object' && !Array.isArray(json) && typeof (json as { code?: unknown }).code === 'number') {
    const result = json as { code: number; message?: string; data?: T };
    if (result.code !== 200) {
      throw Object.assign(new Error(result.message || '上传失败'), { status: resp.status, code: result.code });
    }
    return result.data as T;
  }

  return json as T;
}
